import { HTTPError } from 'ky';
import { http } from '@/lib/http';
import type { SubCase } from '@/lib/sub-cases/types';
import type {
  ICareEntitlementView,
  ICaseFlightDelay,
  IDelayDeclarationBody,
  IDelayDeclarationResult,
  IDelayReleaseBody,
  IDelayReleaseResult,
  IMealIssueBody,
  IMealIssueResult,
  ILoadCareEntitlementResult,
  IOutOfPolicyDismissBody,
} from './types';

// The operator-bff flight-delay routes (flight-delay B1, consumed by U1). `http` is the
// single configured ky instance (frontend-conventions §6.4); paths are relative because
// ky's `prefixUrl` requires it.
//
//   GET  /v1/disruptions/:urn/delays        → ICaseFlightDelay[]
//   GET  /v1/cases/:urn/delay               → ICaseFlightDelay | 404 delay_not_found (→ null here)
//   POST /v1/delay-declarations             → IDelayDeclarationResult  (Idempotency-Key required)
//   GET  /v1/cases/:urn/care-entitlements   → ICareEntitlementView[]   (AIRPORT_OPERATOR+)
//   POST /v1/care-entitlements/load         → ILoadCareEntitlementResult (AIRPORT_OPERATOR+)
//   POST /v1/cases/:urn/delay/release       → IDelayReleaseResult (OPS_SUPERVISOR / ADMIN)
//   POST /v1/cases/:urn/delay/meals/issue   → IMealIssueResult («Dar la comida ya», OPS_SUPERVISOR / ADMIN)
//   GET  /v1/cases/:urn/sub-cases           → SubCase[] (the release result's group labels)
//   POST /v1/cases/:urn/delay/out-of-policy/dismiss → ICaseFlightDelay (delay-limits; AIRPORT_OPERATOR+)

/**
 * The client must outwait the BFF's own declaration budget (15 s: the declaration runs
 * the signal pipeline, which may open the disruption + case). A client that gave up
 * first would report a failure for a declaration that then lands — harmless thanks to
 * the Idempotency-Key, but confusing.
 */
const DECLARATION_TIMEOUT_MS = 20_000;

/**
 * The release resets the groups AND asks rebooking for a run before it answers — the
 * same reasoning as the declaration: outwait the BFF so a slow answer is not reported
 * as a failure of a release that landed.
 */
const RELEASE_TIMEOUT_MS = 20_000;

/**
 * A refused delay write, with the BFF / domain error code (`estimated_departure_required`,
 * `idempotency_key_reused`, `upstream_unavailable`, …) and the field it names, so the
 * form can say WHY next to the right input. `retryable` = the same submission may be
 * sent again with the SAME Idempotency-Key (a 5xx, a timeout, an in-flight claim).
 * `limitMinutes` = the maximum a `400 delay_exceeds_limit` names (delay-limits).
 */
export class DelayRequestError extends Error {
  readonly status: number;
  readonly code: string;
  readonly field: string | undefined;
  readonly limitMinutes: number | undefined;
  constructor(status: number, code: string, field?: string, limitMinutes?: number) {
    super(code);
    this.name = 'DelayRequestError';
    this.status = status;
    this.code = code;
    this.field = field;
    this.limitMinutes = limitMinutes;
  }
  get retryable(): boolean {
    return this.status === 0 || this.status >= 500 || this.code === 'declaration_in_progress';
  }
}

/** A failed delay write → the i18n key of its sentence (`disruptions` ns) + the raw code. */
export function delayErrorKey(err: unknown): { key: string; code: string } {
  const code = err instanceof DelayRequestError ? err.code : 'network_error';
  return { key: `delay.form.errors.server.${code}`, code };
}

/** Codes the release surface names; anything else reads as the generic sentence. */
const RELEASE_CODES: ReadonlySet<string> = new Set([
  'forbidden',
  'role_forbidden',
  'delay_not_found',
  'not_releasable',
  'reason_required',
  'upstream_unavailable',
  'network_error',
  'http_403',
]);

/** A failed release → the i18n key of its sentence (`disruptions` ns) + the raw code. */
export function releaseErrorKey(err: unknown): { key: string; code: string } {
  const code = err instanceof DelayRequestError ? err.code : 'network_error';
  return {
    key: RELEASE_CODES.has(code) ? `delay.release.errors.${code}` : 'delay.release.errors.generic',
    code,
  };
}

/** Codes «Dar la comida ya» names; anything else reads as the generic sentence. */
const MEAL_ISSUE_CODES: ReadonlySet<string> = new Set([
  'forbidden',
  'role_forbidden',
  'delay_not_found',
  'not_delayed',
  'meal_force_disabled',
  'invalid_reason',
  'upstream_unavailable',
  'network_error',
]);

/** A failed forced meal issue → the i18n key of its sentence (`operateDelay` ns) + the raw code. */
export function mealIssueErrorKey(err: unknown): { key: string; code: string } {
  const code = err instanceof DelayRequestError ? err.code : 'network_error';
  return {
    key: MEAL_ISSUE_CODES.has(code) ? `forceMeal.errors.${code}` : 'forceMeal.errors.generic',
    code,
  };
}

async function errorOf(response: Response): Promise<DelayRequestError> {
  type Body = { error?: unknown; field?: unknown; limitMinutes?: unknown };
  let body: Body | null = null;
  try {
    body = (await response.json()) as Body;
  } catch {
    body = null;
  }
  // A domain code is snake_case (`estimated_departure_required`); Nest's default bodies
  // carry a reason phrase there (`Forbidden`, `Bad Request`) — those fall back to the status.
  const code =
    typeof body?.error === 'string' && /^[a-z][a-z0-9_]*$/.test(body.error)
      ? body.error
      : `http_${response.status}`;
  const field = typeof body?.field === 'string' ? body.field : undefined;
  const limit = body?.limitMinutes;
  return new DelayRequestError(
    response.status,
    code,
    field,
    typeof limit === 'number' && limit > 0 ? limit : undefined,
  );
}

/**
 * Run a write and turn every failure into a `DelayRequestError`. `throwHttpErrors:
 * false` keeps the error BODY readable here (the shared client's `beforeError` hook
 * consumes it otherwise) — the 401 path still runs, it lives in `afterResponse`.
 */
async function write<T>(send: () => Promise<Response>): Promise<T> {
  let response: Response;
  try {
    response = await send();
  } catch {
    // Timeout / network: nothing reached us, the same key may be retried.
    throw new DelayRequestError(0, 'network_error');
  }
  if (!response.ok) throw await errorOf(response);
  return (await response.json()) as T;
}

export async function fetchDisruptionDelays(disruptionUrn: string): Promise<ICaseFlightDelay[]> {
  const rows = await http
    .get(`v1/disruptions/${encodeURIComponent(disruptionUrn)}/delays`)
    .json<ICaseFlightDelay[]>();
  return Array.isArray(rows) ? rows : [];
}

/** A case's timeline, or `null` when it has none (`404 delay_not_found` is a state, not an error). */
export async function fetchCaseDelay(caseUrn: string): Promise<ICaseFlightDelay | null> {
  try {
    return await http.get(`v1/cases/${encodeURIComponent(caseUrn)}/delay`).json<ICaseFlightDelay>();
  } catch (err) {
    if (err instanceof HTTPError && err.response.status === 404) return null;
    throw err;
  }
}

/**
 * Declare a delay / cancellation / departure / recovery by hand. `idempotencyKey` is
 * the SUBMISSION's key (see `declaration.ts` `createSubmissionKeys`): the same key on a
 * retry of the same submission replays the stored answer instead of declaring twice.
 */
export async function postDelayDeclaration(
  body: IDelayDeclarationBody,
  idempotencyKey: string,
): Promise<IDelayDeclarationResult> {
  return write<IDelayDeclarationResult>(() =>
    http.post('v1/delay-declarations', {
      json: body,
      headers: { 'Idempotency-Key': idempotencyKey },
      timeout: DECLARATION_TIMEOUT_MS,
      throwHttpErrors: false,
    }),
  );
}

export async function fetchCareEntitlements(caseUrn: string): Promise<ICareEntitlementView[]> {
  const rows = await http
    .get(`v1/cases/${encodeURIComponent(caseUrn)}/care-entitlements`)
    .json<ICareEntitlementView[]>();
  return Array.isArray(rows) ? rows : [];
}

/** Load one PENDING / FAILED entitlement onto the group's card (idempotent on its key at the wallet). */
export async function loadCareEntitlement(
  entitlementKey: string,
): Promise<ILoadCareEntitlementResult> {
  return write<ILoadCareEntitlementResult>(() =>
    http.post('v1/care-entitlements/load', {
      json: { entitlementKey },
      throwHttpErrors: false,
    }),
  );
}

/** The case's PNR groups — the same route and cache entry `useSubCasesForCase` reads. */
export async function fetchCaseSubCases(caseUrn: string): Promise<SubCase[]> {
  const rows = await http
    .get(`v1/cases/${encodeURIComponent(caseUrn)}/sub-cases`)
    .json<SubCase[]>();
  return Array.isArray(rows) ? rows : [];
}

/**
 * Release the case's delay hold to Nexa's rebooking (OPS_SUPERVISOR / ADMIN). Deliberately
 * no Idempotency-Key: the release is idempotent by state (cases `flight-delay.tds.md`
 * §5.3 — a repeat with nothing new to release answers the stored release, and re-triggers
 * a failed run with the same bulk key), so a retry after `rebooking.triggered: false`
 * reaches the rebooking trigger again; a client key would replay the stored failure.
 * Case-wide: the body is the reason alone (`400 partial_release_unsupported` answers a
 * group subset, which this client never sends — it reads as the generic error).
 * `409 not_releasable` = the flight is no longer `DELAYED` (departed, recovered, escalated).
 */
export async function postDelayRelease(
  caseUrn: string,
  body: IDelayReleaseBody,
): Promise<IDelayReleaseResult> {
  return write<IDelayReleaseResult>(() =>
    http.post(`v1/cases/${encodeURIComponent(caseUrn)}/delay/release`, {
      json: body,
      timeout: RELEASE_TIMEOUT_MS,
      throwHttpErrors: false,
    }),
  );
}

/**
 * «Dar la comida ya» (meal timing, 2026-10-30; OPS_SUPERVISOR / ADMIN): under
 * `care.delay.mealTiming = 'REAL_WAIT'`, issue now every meal tier the current forecast covers.
 * Idempotent at cases (nothing left → `issuedTiers: []`), so no Idempotency-Key. `409
 * not_delayed` = the flight is no longer a delay (departed, recovered, cancelled).
 */
export async function postMealIssue(
  caseUrn: string,
  body: IMealIssueBody,
): Promise<IMealIssueResult> {
  return write<IMealIssueResult>(() =>
    http.post(`v1/cases/${encodeURIComponent(caseUrn)}/delay/meals/issue`, {
      json: body,
      throwHttpErrors: false,
    }),
  );
}

/**
 * «Pasar ya a Nexa» — end the airline's grace window of an escalated case before it is
 * due (cases' early grace release: the airline's reassignments stay, the rest go to
 * rebooking in ONE run). Idempotent at cases (a released window answers REPLAY), so no
 * Idempotency-Key. `409 not_releasable` = the case is no longer in its window.
 */
export async function postGraceRelease(caseUrn: string): Promise<{ status: string }> {
  return write<{ status: string }>(() =>
    http.post(`v1/cases/${encodeURIComponent(caseUrn)}/delay/grace/release`, {
      json: {},
      timeout: RELEASE_TIMEOUT_MS,
      throwHttpErrors: false,
    }),
  );
}

/**
 * Dismiss the case's out-of-policy provider report (delay-limits) — the operator read it
 * and keeps the flight as it is. Idempotent at cases (an already-clear flag answers the
 * timeline with no write), so no Idempotency-Key; the answer is the timeline.
 */
export async function postOutOfPolicyDismiss(
  caseUrn: string,
  body: IOutOfPolicyDismissBody,
): Promise<ICaseFlightDelay> {
  return write<ICaseFlightDelay>(() =>
    http.post(`v1/cases/${encodeURIComponent(caseUrn)}/delay/out-of-policy/dismiss`, {
      json: body,
      throwHttpErrors: false,
    }),
  );
}
